import React, { useState } from 'react';
import { Link } from 'wouter';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { Menu, X } from 'lucide-react';
import { BlurPresets } from '@/utils/blurEffects';

const PublicNav = React.memo(function PublicNav() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-cyber-purple text-white px-4 py-2 rounded-[1.3rem] focus:ring-2 focus:ring-cyber-cyan"
      >
        Skip to main content
      </a>
      <nav
        className={`fixed top-[var(--nav-offset)] left-0 right-0 z-40 h-24 transition-[top] duration-300 ${BlurPresets.navbar}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <span className="font-serif text-3xl font-medium tracking-wide text-foreground">
                BRITE<span className="text-brite-terra">.</span>
              </span>
            </Link>

            {/* Desktop Nav + Auth Section */}
            <div className="hidden md:flex items-center space-x-3">
              <Link href="/sign-in">
                <UnifiedButton
                  variant="ghost"
                  size="md"
                  className="text-foreground border border-brite-line hover:border-brite-terra hover:text-brite-terra rounded-full px-6 py-2 font-medium transition-all duration-300"
                  data-testid="button-sign-in"
                >
                  <span>Sign In</span>
                </UnifiedButton>
              </Link>
              <Link href="/enroll">
                <UnifiedButton
                  variant="primary"
                  size="md"
                  className="bg-brite-ink text-brite-paper hover:bg-brite-terra rounded-full font-medium px-6 py-2 transition-all duration-300"
                  data-testid="button-enroll-nav"
                >
                  <span>Enroll</span>
                </UnifiedButton>
              </Link>
            </div>

            {/* Mobile menu button */}
            <UnifiedButton
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-foreground hover:text-brite-terra p-2"
              data-testid="button-mobile-menu"
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" aria-hidden="true" />
              ) : (
                <Menu className="w-5 h-5" aria-hidden="true" />
              )}
            </UnifiedButton>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            className={`md:hidden ${BlurPresets.navbarDropdown}`}
            role="region"
            aria-label="Mobile navigation menu"
          >
            <div className="px-4 py-4 space-y-3">
              <Link href="/sign-in" onClick={() => setIsMobileMenuOpen(false)}>
                <UnifiedButton
                  variant="ghost"
                  size="lg"
                  fullWidth
                  className="text-foreground border border-brite-line hover:border-brite-terra hover:text-brite-terra rounded-full font-medium px-6 py-3 transition-all duration-300"
                  data-testid="button-mobile-sign-in"
                >
                  <span>Sign In</span>
                </UnifiedButton>
              </Link>
              <Link href="/enroll" onClick={() => setIsMobileMenuOpen(false)}>
                <UnifiedButton
                  variant="primary"
                  size="lg"
                  fullWidth
                  className="bg-brite-ink text-brite-paper hover:bg-brite-terra rounded-full font-medium px-6 py-3"
                  data-testid="button-mobile-enroll"
                >
                  <span>Enroll</span>
                </UnifiedButton>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
});

export default PublicNav;
